import tokens from '@kluein/design-tokens';
import {
  Box,
  Button,
  Heading,
  Modal,
  RadioButtonGroup,
  TextInput,
} from '@kluein/klue-ui';
import { X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import TEST_IDS from 'test-ids';

import { type BoardTypeType, useBoardWizard } from './hooks/useBoardWizard';
import { CompanyPicker } from './partials/CompanyPicker';
import { TemplatePicker } from './partials/TemplatePicker';

type BoardWizardProps = {
  onClose: () => void;
  allowBlank?: boolean;
  useTemplatePicker?: boolean;
};
export function BoardWizard({
  allowBlank = false,
  useTemplatePicker = false,
  onClose,
}: BoardWizardProps) {
  const { t } = useTranslation();
  const {
    isLoading,
    boardCategoryOptions,
    boardCategory,
    onBoardCategoryChange,
    setCompany,
    companyUrl,
    setCompanyUrl,
    boardName,
    setBoardName,
    setCopyFromId,
    showCompanyPicker,
    showCompanyUrl,
    showBoardName,
    showTemplatePicker,
    isSubmitEnabled,
    onCreateBoard,
  } = useBoardWizard({ allowBlank, useTemplatePicker });

  return (
    <Modal onClose={onClose}>
      <Modal.Content width={{ min: '400px' }} pad="0">
        <Box
          pad={{ vertical: 'small', horizontal: 'large' }}
          direction="row"
          justify="between"
          align="center"
        >
          <Heading level={3} margin="0px" style={{ border: 'none' }}>
            {t('Common:boardWizard.title')}
          </Heading>

          <X
            size="16px"
            color={tokens.color.neutral[800]}
            onClick={onClose}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <Box
          height={{ min: '214px' }}
          pad={{ vertical: 'xsmall', horizontal: 'large' }}
          border={{
            side: 'horizontal',
            size: '1px',
            color: tokens.color.primitives.gray[75],
          }}
        >
          <FormGroup>
            <Heading level={2}>{t('Common:boardWizard.categoryLabel')}</Heading>
            <RadioButtonGroup
              direction="row"
              // eslint-disable-next-line i18next/no-literal-string
              name="board-category"
              options={boardCategoryOptions}
              value={boardCategory}
              onChange={(event) => {
                const newBoardType = event.target.value as BoardTypeType;
                onBoardCategoryChange(newBoardType);
              }}
              data-test-id={TEST_IDS.boardWizard.boardCategoryRadioGroup}
            />
          </FormGroup>

          {showCompanyPicker && (
            <FormGroup>
              <Heading level={2}>
                {t('Common:boardWizard.companyLabel')}
              </Heading>
              <CompanyPicker
                onChangeName={(name) => setBoardName(name)}
                onPick={(company) => {
                  setCompany(company);
                }}
              />
            </FormGroup>
          )}

          {showCompanyUrl && (
            <Box pad={{ vertical: 'small' }}>
              <Heading level={3} size="small">
                {t('Common:boardWizard.companyUrlLabel')}
              </Heading>
              <TextInput
                value={companyUrl}
                onChange={(event) => setCompanyUrl(event.target.value)}
                placeholder="https://"
                data-test-id={TEST_IDS.boardWizard.companyUrlTextInput}
              />
            </Box>
          )}

          {showBoardName && (
            // TODO companyUrl is valid ?
            <FormGroup>
              <Heading level={2}>{t('Common:boardWizard.boardLabel')}</Heading>
              <TextInput
                value={boardName}
                onChange={(event) => setBoardName(event.target.value)}
                data-test-id={TEST_IDS.boardWizard.boardNameTextInput}
              />
            </FormGroup>
            // TODO Board image / logo
          )}

          {showTemplatePicker && (
            <FormGroup>
              <Heading level={2}>
                {t('Common:boardWizard.templateLabel')}
              </Heading>
              <TemplatePicker
                onPick={(templateId) => setCopyFromId(templateId)}
              />
            </FormGroup>
          )}
        </Box>

        <Box
          pad={{ vertical: 'small', horizontal: 'large' }}
          fill="horizontal"
          direction="row"
          align="center"
          justify="end"
          gap="medium"
        >
          <Button
            variant="flat-form"
            size="small"
            label={t('Common:actions.cancel')}
            onClick={onClose}
          />
          <Button
            variant="secondary"
            size="small"
            label={t('Common:boardWizard.submitButton')}
            loading={isLoading}
            onClick={() => onCreateBoard().then(onClose)}
            disabled={!isSubmitEnabled}
            data-test-id={TEST_IDS.boardWizard.submitButton}
          />
        </Box>
      </Modal.Content>
    </Modal>
  );
}

function FormGroup({ children }: { children: React.ReactNode }) {
  return <Box margin={{ vertical: 'small' }}>{children}</Box>;
}
